import { useEffect, useState } from "react";
import {
  createOfflineLicenseRequest,
  createOrgRequest,
  deleteOrgRequest,
  getOrgsOfDistributor,
  getOrgsRequests,
} from "@utils/api-communication/RestApiCli";
import {
  Button,
  Card,
  Container,
  Drawer,
  Flex,
  Title,
  Text,
  Divider,
  Group,
  Switch,
  Menu,
  Burger,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  OrganizationAccountRequestForm,
  showCreateOrgRequestError,
} from "./OrganizationAccountRequestForm";
import OrganizationRequestList from "./OrganizationRequestList";
import OrganizationManagementDashboard from "../organization_dashboard/OrganizationManagementDashboard";
import OfflineLicenseRequestForm from "./OfflineLicenseRequestForm";
import OfflineLicenseRequestList from "./OfflineLicenseRequestList";
import { useTranslation } from "react-i18next";
import SubmissionsList from "@components/submissions/SubmissionsList";
import PaginatedOrganizations from "../common/PaginatedOrganizations";
import { OrganizationListRow } from "../common/organizationListQuery";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserDistributorId } from "@utils/redux-utils/selectors/userSelectors";
import { sortByCreationDateDesc } from "@utils/CommonUtil";
import { DISTRIBUTOR_DOCS_FOLDER_URL } from "@constants/externalTutorialLinks";

export default function DistributorDashboard(_props) {
  const [organizations, setOrganizations] = useState([]);
  const [distId, setDistId] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [orgRequests, setOrgRequests] = useState([]);
  const [openedOrgRequest, { open: openOrgRequest, close: closeOrgRequest }] = useDisclosure(false);
  const [openedOfflineRequest, { open: openOfflineRequest, close: closeOfflineRequest }] =
    useDisclosure(false);
  const [loadedOrgsDistId, setLoadedOrgsDistId] = useState(null);
  const [selectedRow, setSelectedRow] = useState<OrganizationListRow | null>(null);
  const [menuOpened, setMenuOpened] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userDistId = useSelector(selectUserDistributorId);

  const urlDistId = searchParams.get("id") ?? "";
  const selectedOrgId = searchParams.get("orgId");
  const selectedOrganization =
    selectedRow && selectedRow.orgId === selectedOrgId
      ? selectedRow
      : (organizations.find((org) => org.orgId === selectedOrgId) ?? null);

  useEffect(() => {
    const id = searchParams.get("id");
    if (!id && userDistId) {
      const params = new URLSearchParams(searchParams);
      params.set("id", userDistId);
      navigate(
        { pathname: "/distributor-dashboard", search: params.toString() },
        { replace: true },
      );
      return;
    }
    setDistId(id ? id : "");
  }, [searchParams, openedOrgRequest, userDistId]);

  useEffect(() => {
    if (distId != null) {
      updateLists();
    }
  }, [distId]);

  // urlDistId, not distId: the state lags a URL change by a render, which was enough
  // to judge a new orgId against the previous distributor's list
  useEffect(() => {
    if (!selectedOrgId || loadedOrgsDistId === null || loadedOrgsDistId !== urlDistId) return;
    if (!selectedOrganization) {
      updateSelectedOrganization(null);
    }
  }, [selectedOrgId, selectedOrganization, loadedOrgsDistId, urlDistId]);

  useEffect(() => {
    setSelectedRow((current) => {
      if (!current) return current;
      const fresh = organizations.find((org) => org.orgId === current.orgId);
      return fresh ? { ...current, ...fresh } : current;
    });
  }, [organizations]);

  const updateSelectedOrganization = (orgId) => {
    const params = new URLSearchParams(searchParams);
    if (orgId) {
      params.set("orgId", orgId);
    } else {
      params.delete("orgId");
    }
    setSearchParams(params);
  };

  const updateLists = () => {
    getOrgsOfDistributor(
      distId ? { distId: distId } : {},
      (res) => {
        setOrganizations(sortByCreationDateDesc(res.data));
        setLoadedOrgsDistId(distId);
      },
      (error) => {
        console.error(error);
      },
    );

    getOrgsRequests(
      distId ? { id: distId } : {},
      (res) => {
        setOrgRequests(res.data);
      },
      (error) => {
        console.error(error);
      },
    );
  };

  const handleDeleteRequest = (id) => {
    deleteOrgRequest(
      { id },
      (res) => {
        updateLists();
        console.log(res);
      },
      (error) => {
        console.error(error);
      },
    );
  };

  return (
    <Container fluid>
      {selectedOrganization ? (
        <OrganizationManagementDashboard
          organization={selectedOrganization}
          back={() => {
            setSelectedRow(null);
            updateSelectedOrganization(null);
            updateLists();
          }}
          updateOrganization={() => updateLists()}
        />
      ) : (
        <Container>
          <Drawer
            opened={openedOrgRequest}
            onClose={closeOrgRequest}
            withCloseButton={true}
            position="right"
            size="lg"
          >
            <OrganizationAccountRequestForm
              onSubmit={(body) => {
                createOrgRequest(
                  body,
                  distId,
                  (res) => {
                    console.log(res);
                    closeOrgRequest();
                    updateLists();
                  },
                  (error) => {
                    console.error(error);
                    showCreateOrgRequestError(error, t);
                  },
                );
              }}
            />
          </Drawer>
          <Drawer
            opened={openedOfflineRequest}
            onClose={closeOfflineRequest}
            withCloseButton={true}
            position="right"
            size="lg"
          >
            <OfflineLicenseRequestForm
              organizations={organizations}
              onSubmit={(body, setLoading) => {
                setLoading(true);
                createOfflineLicenseRequest(
                  body,
                  (res) => {
                    console.log(res);
                    setLoading(false);
                    closeOfflineRequest();
                    updateLists();
                  },
                  (error) => {
                    setLoading(false);
                    console.error(error);
                  },
                );
              }}
            />
          </Drawer>
          <Flex justify={"space-between"} align={"center"}>
            <Group>
              <Menu
                opened={menuOpened}
                onChange={setMenuOpened}
                shadow="md"
                width={200}
                position="bottom-start"
              >
                <Menu.Target>
                  <Burger
                    opened={menuOpened}
                    onClick={() => setMenuOpened((o) => !o)}
                    size="sm"
                    mr="md"
                  />
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => navigate("/analytics-dashboard")}>
                    Analytics Dashboard
                  </Menu.Item>
                  <Menu.Item
                    component="a"
                    href={DISTRIBUTOR_DOCS_FOLDER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Docs
                  </Menu.Item>
                  <Menu.Item onClick={() => navigate("/orders")}>Orders</Menu.Item>
                  <Menu.Item onClick={() => navigate("/licenses")}>Licenses</Menu.Item>
                  <Menu.Item onClick={() => navigate("/invoices")}>Invoices</Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <Title my={"sm"}>Distributor Dashboard</Title>
            </Group>
            <Group>
              <Button onClick={openOrgRequest}>New Account Request</Button>
              <Button onClick={openOfflineRequest}>Offline License Request</Button>
            </Group>
          </Flex>
          <SubmissionsList />
          <OfflineLicenseRequestList changed={openedOfflineRequest} />
          {orgRequests.length > 0 && (
            <>
              <Card mt={"sm"} p={"sm"} style={{ background: "var(--oculera-surface-alt)" }}>
                <OrganizationRequestList
                  orgRequests={orgRequests}
                  deleteRequest={handleDeleteRequest}
                />
              </Card>
              <Divider size={"sm"} mt={"sm"} mb={"sm"} />
            </>
          )}
          <Group justify="space-between">
            <Text mt={"sm"}> Organizations </Text>
            <Switch
              onChange={() => setShowDetails(!showDetails)}
              onLabel={t("Show")}
              offLabel={t("Hide")}
            />
          </Group>
          <PaginatedOrganizations
            distId={distId}
            showDetails={showDetails}
            onOrganizationSelect={(organization) => {
              setSelectedRow(organization);
              updateSelectedOrganization(organization.orgId);
            }}
            dateLocale="en-GB"
          />
        </Container>
      )}
    </Container>
  );
}
